import React from 'react'
import { Form, Input, Button, Select, message } from 'antd'
import { ArrowLeftOutlined, DownOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { resourceApi, UpdateResourceRequest } from '../services/resources'


const { Option } = Select

const EditResource = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm()

  // Fetch the resource to edit
  const { data: resourceData, isLoading: resourceLoading } = useQuery({
    queryKey: ['resource', id],
    queryFn: () => resourceApi.getResourceById(id!),
    select: (data) => {
     
      if (data.success) {
        // Handle both possible response structures
        return data.data.resource || data.data || data
      }
      return null
    },
    enabled: !!id,
    staleTime: 0, // Force fresh data
    gcTime: 0 // Don't cache
  })

  

  // Update resource mutation
  const updateMutation = useMutation({
    mutationFn: (updateData: UpdateResourceRequest) => resourceApi.updateResource(id!, updateData),
    onSuccess: () => {
      message.success('Resource updated successfully!')
      navigate('/resources')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update resource')
    },
  })

  const onFinish = (values: any) => {
    const updateData: UpdateResourceRequest = {
      title: values.title,
      description: values.description || '',
      resourceType: values.resourceType,
      link: values.link
      // categoryId is not included since it shouldn't be changed
    }
    
    updateMutation.mutate(updateData)
  }

  // Pre-populate form when resource data is loaded
  React.useEffect(() => {
    if (resourceData) {
      if (
        typeof resourceData === 'object' &&
        resourceData !== null &&
        'title' in resourceData &&
        'description' in resourceData &&
        'resourceType' in resourceData &&
        'link' in resourceData
      ) {
      
        form.setFieldsValue({
          title: (resourceData as any).title,
          description: (resourceData as any).description,
          resourceType: (resourceData as any).resourceType,
          link: (resourceData as any).link
          // categoryId is not set since it's read-only
        })
      }
    }
  }, [resourceData, form])

  if (resourceLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
              <div className="space-y-6">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-12"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!resourceData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <p className="text-red-600 mb-4">Resource not found</p>
              <Button onClick={() => navigate('/resources')}>Back to Resources</Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Back Button */}
      <div className="mb-6">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 p-2 h-auto font-medium"
        >
          Back
        </Button>
      </div>

      {/* Form Card */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-8">Edit Resource</h1>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className=""
          >
            {/* Title */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Title*</span>}
              name="title"
              rules={[{ required: true, message: 'Please enter title' }]}
              required={false}
            >
              <Input 
                placeholder="Enter title"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Description */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Description</span>}
              name="description"
            >
              <Input.TextArea 
                placeholder="Enter description"
                rows={3}
                className="rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

          

            {/* Resource Type */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Resource Type*</span>}
              name="resourceType"
              rules={[{ required: true, message: 'Please select a resource type' }]}
              required={false}
            >
              <Select
                placeholder="Select a Resource Type"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                suffixIcon={<DownOutlined className="text-gray-400" />}
              >
                <Option value="guide">Guide</Option>
                <Option value="tutorial">Tutorial</Option>
                <Option value="documentation">Documentation</Option>
                <Option value="video">Video</Option>
                <Option value="article">Article</Option>
              </Select>
            </Form.Item>

            {/* Link */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Link*</span>}
              name="link"
              rules={[
                { required: true, message: 'Please enter link' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
              required={false}
            >
              <Input 
                placeholder="Add link"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="mb-0">
              <Button 
                type="primary" 
                htmlType="submit"
                loading={updateMutation.isPending}
                className="w-full h-12 bg-primary hover:bg-primary/90 border-primary hover:border-primary/90 rounded-lg font-medium text-base"
              >
                Update Resource
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default EditResource
